import apiService from 'frontcore/data/api-service/api-service'
import i18n from 'frontcore/i18n'
import useAllowedPerimetersStore from 'frontcore/store/useAllowedPerimetersStore'
import { defineStore } from 'pinia'
import { LoginTypeOptions } from 'frontcore/domain/authorization/model/types'
import { Nullable } from 'frontcore/presentation/types'
import { ProfilePasswordExpiredError } from 'frontcore/errors/profile'
import { StoreRequestStatus } from 'frontcore/store/types'

import type { RoleModule } from 'frontcore/entities/permission/types'

const URL_BASE_PROFILE = `profile/`

export class ProfileAPIError extends Error {}

type PermissionAPIResponse = {
  is_overall_manager: boolean
  is_overall_operator: boolean
  is_overall_approve_supervisor: boolean
  is_overall_create_supervisor: boolean
}

export type ProfileAPIResponse = {
  id: number
  children: number[]
  descendant_roles: number[]
  employee_id: string | null
  expired_password: boolean
  is_administrator: boolean
  is_problem_planificator: boolean
  is_problem_supervisor: boolean
  can_edit_skill_matrix: boolean
  login_name: string
  login_type: LoginTypeOptions
  name: string
  parent: number | null
  perimeter_ids_managed: number[]
  perimeters_as_manager_without_descendants: number[]
  perimeters_as_consolidate_supervisor: number[]
  perimeters_as_create_supervisor: number[]
  permission: PermissionAPIResponse
  role: number
  role_modules: RoleModule[]
}

interface IStateProfileStore {
  id: number
  allManagedPerimeterIds: Readonly<number[]>
  childrenIds: number[]
  descendantRoles: number[]
  employeeId: string
  isAdministrator: boolean
  isOverallManager: boolean
  isOverallApproveSupervisor: boolean
  isOverallCreateSupervisor: boolean
  isProblemPlanificator: boolean
  isProblemSupervisor: boolean
  canEditSkillMatrix: boolean
  loginName: string
  loginType: LoginTypeOptions | null
  parentId: number | null
  perimeterIdsManaged: number[]
  perimetersAsManagerWithoutDescendants: number[]
  perimeterIdsAsApprovalSupervisor: number[]
  perimeterIdsAsCreateSupervisor: number[]
  roleId: number | null
  roleModules: RoleModule[]
  userName: string
  status: StoreRequestStatus
}

const emptyProfileValues: IStateProfileStore = {
  id: 0,
  allManagedPerimeterIds: [],
  childrenIds: [],
  descendantRoles: [],
  employeeId: '',
  isAdministrator: false,
  isOverallManager: false,
  isOverallApproveSupervisor: false,
  isOverallCreateSupervisor: false,
  isProblemPlanificator: false,
  isProblemSupervisor: false,
  canEditSkillMatrix: false,
  loginName: '',
  loginType: null,
  parentId: null,
  perimeterIdsManaged: [],
  perimetersAsManagerWithoutDescendants: [],
  perimeterIdsAsApprovalSupervisor: [],
  perimeterIdsAsCreateSupervisor: [],
  roleId: null,
  roleModules: [],
  userName: '',
  status: StoreRequestStatus.idle,
}

export const useProfileStore = defineStore('profile', {
  state: (): IStateProfileStore => ({ ...emptyProfileValues }),
  actions: {
    async fetchProfile () {
      try {
        const response = await apiService
          .buildRequest()
          .withSessionToken()
          .get<ProfileAPIResponse>(URL_BASE_PROFILE)
        return response.data
      } catch {
        const { t } = i18n.global
        this.status = StoreRequestStatus.idle
        throw new ProfileAPIError(t('login.errors.not_authorized_profile'))
      }
    },
    async setAllManagedPerimeters () {
      const allowedPerimetersStore = useAllowedPerimetersStore()
      await allowedPerimetersStore.fetch()
      this.allManagedPerimeterIds = allowedPerimetersStore.allowedIds
    },
    parseAPIResponse (profile: ProfileAPIResponse) {
      this.id = profile.id
      this.childrenIds = profile.children
      this.descendantRoles = profile.descendant_roles
      this.isAdministrator = profile.is_administrator
      this.isOverallManager = profile.permission.is_overall_manager
      this.isOverallApproveSupervisor = profile.permission?.is_overall_approve_supervisor ?? false
      this.isOverallCreateSupervisor = profile.permission?.is_overall_create_supervisor ?? false
      this.isProblemPlanificator= profile.is_problem_planificator
      this.isProblemSupervisor= profile.is_problem_supervisor
      this.canEditSkillMatrix = profile.can_edit_skill_matrix
      this.loginName = profile.login_name
      this.loginType = profile.login_type
      this.parentId = profile.parent
      this.perimeterIdsManaged = profile.perimeter_ids_managed
      this.perimetersAsManagerWithoutDescendants = profile.perimeters_as_manager_without_descendants
      this.perimeterIdsAsApprovalSupervisor = profile.perimeters_as_consolidate_supervisor
      this.perimeterIdsAsCreateSupervisor = profile.perimeters_as_create_supervisor
      this.roleId = profile.role
      this.roleModules = profile.role_modules
      this.userName = profile.name
      this.employeeId = profile.employee_id ?? ''
    },

    async fetch () {
      this.status = StoreRequestStatus.loading
      const profile = await this.fetchProfile()
      if (profile.expired_password) {
        throw new ProfilePasswordExpiredError(profile.id)
      }
      this.parseAPIResponse(profile)
      await this.setAllManagedPerimeters()
      this.status = StoreRequestStatus.succeeded
      return profile
    },
    clear () {
      this.id = emptyProfileValues.id
      this.allManagedPerimeterIds = emptyProfileValues.allManagedPerimeterIds
      this.canEditSkillMatrix = emptyProfileValues.canEditSkillMatrix
      this.childrenIds = emptyProfileValues.childrenIds
      this.descendantRoles = emptyProfileValues.descendantRoles
      this.employeeId = emptyProfileValues.employeeId
      this.isAdministrator = emptyProfileValues.isAdministrator
      this.isOverallApproveSupervisor = emptyProfileValues.isOverallApproveSupervisor
      this.isOverallCreateSupervisor = emptyProfileValues.isOverallCreateSupervisor
      this.isOverallManager = emptyProfileValues.isOverallManager
      this.isProblemPlanificator = emptyProfileValues.isProblemPlanificator
      this.isProblemSupervisor = emptyProfileValues.isProblemSupervisor
      this.loginName = emptyProfileValues.loginName
      this.loginType = emptyProfileValues.loginType
      this.parentId = emptyProfileValues.parentId
      this.perimeterIdsManaged = emptyProfileValues.perimeterIdsManaged
      this.perimetersAsManagerWithoutDescendants = emptyProfileValues.perimetersAsManagerWithoutDescendants
      this.roleId = emptyProfileValues.roleId
      this.roleModules = emptyProfileValues.roleModules
      this.status = StoreRequestStatus.idle
      this.userName = emptyProfileValues.userName
    },
  },
  getters: {
    hasPermissionInPerimeter (state: IStateProfileStore) {
      return (perimeterId: Nullable<number>): boolean => {
        if (state.isOverallManager && perimeterId) {
          return true
        }
        return perimeterId ? state.perimeterIdsManaged.includes(perimeterId) : false
      }
    },
    userIsLogged (state: IStateProfileStore) {
      return state.status === StoreRequestStatus.succeeded
    },
    userHasAccessPermission (state: IStateProfileStore) {
      return (
        state.isAdministrator ||
        state.perimeterIdsManaged.length > 0 ||
        state.isOverallManager
      )
    },
    userIsProblemPlanificator (state: IStateProfileStore) {
      return state.isProblemPlanificator
    },
    hasRoleModuleAccess: (state: IStateProfileStore) => (moduleName: string): boolean => {
      return state.roleModules.some(({ name }) => moduleName === name)
    },
  },
})
